import { CheckTreePicker } from "rsuite";
import { useDataset } from "../DataSet/DatasetProvider";
import { StandardFilterProps } from "../types";
import { useEffect, useMemo, useRef } from "react";

export interface IDynamicRegionTreePickerProps extends StandardFilterProps {}

// Options arrive flat as { id, text, parentId } from the filter step
// (p_cc_filter_regions) and the tree is built client-side — the same design as
// the Premium plugin's RegionTreeFilter, so the two products stay uniform
// (#75460 / #75486). Selecting a node stores its { id, text } exactly like
// multiSelect does; the monitoring procedures already expand each selected
// region to its subtree (CROSS APPLY f_bzRegion_GetIsolationData), so checking
// a parent covers its descendants without cascading checks in the UI.
type RegionNode = {
  id: string | number;
  text: string;
  parentId?: string | number | null;
};

type RegionTreeNode = RegionNode & { children?: RegionTreeNode[] };

// Build a forest from the flat list. A node whose `parentId` is missing, null,
// or points outside the returned set (an orphan — e.g. a parent beyond the
// user's isolation scope) is rendered as a root rather than dropped.
export const buildRegionTree = (items: RegionNode[]): RegionTreeNode[] => {
  const byId = new Map<string, RegionTreeNode>();
  items.forEach((item) => byId.set(String(item.id), { ...item }));

  const roots: RegionTreeNode[] = [];
  byId.forEach((node) => {
    const parentKey =
      node.parentId === null || node.parentId === undefined
        ? null
        : String(node.parentId);
    const parent = parentKey !== null ? byId.get(parentKey) : undefined;
    if (parent && parent !== node) {
      (parent.children ?? (parent.children = [])).push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
};

const DynamicRegionTreePicker = (props: IDynamicRegionTreePickerProps) => {
  const { getFilterValue, setFilterValue, filtersDataSource } = useDataset();
  const rawValue = getFilterValue(props.filterDef.code);
  const k: string = props.filterDef.code;

  const data = useMemo(
    () => ((filtersDataSource as any)[k] as RegionNode[]) ?? [],
    [filtersDataSource, k],
  );

  const tree = useMemo(() => buildRegionTree(data), [data]);

  const normalizedValue = useMemo(
    () =>
      Array.isArray(rawValue)
        ? rawValue
        : rawValue !== undefined && rawValue !== null && rawValue !== ""
        ? [rawValue]
        : [],
    [rawValue],
  );

  // An initialValue (e.g. {{loggedUserRegion}}) arrives as a plain id; once the
  // options load, upgrade it to the { id, text } shape every other filter uses,
  // so the chip shows a label and the dataset request stays consistent.
  useEffect(() => {
    if (normalizedValue.length === 0 || data.length === 0) {
      return;
    }

    const hasPlainIds = normalizedValue.some(
      (v) => typeof v !== "object" || v === null || !("text" in v),
    );

    if (!hasPlainIds) {
      return;
    }

    const dataMap = new Map<string | number, RegionNode>();
    data.forEach((item) => {
      dataMap.set(item.id, item);
      dataMap.set(String(item.id), item);
      if (typeof item.id === "string" && !isNaN(Number(item.id))) {
        dataMap.set(Number(item.id), item);
      }
    });

    const convertedValues = normalizedValue
      .map((v) => {
        const id = typeof v === "object" && v !== null && "id" in v ? v.id : v;
        return dataMap.get(id);
      })
      .filter((item): item is RegionNode => item !== undefined)
      .map(({ id, text }) => ({ id, text }));

    if (convertedValues.length > 0) {
      setFilterValue(props.filterDef.code, convertedValues);
    }
  }, [data, normalizedValue, props.filterDef.code, setFilterValue]);

  const value = normalizedValue.map((v) =>
    typeof v === "object" && v !== null && "id" in v ? v.id : v,
  );

  const pickerRef = useRef<any>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const scrollParentRef = useRef<HTMLElement | null>(null);
  const scrollHandlerRef = useRef<(() => void) | null>(null);
  const isVertical = props.displayStyle === "vertical";

  const addScrollListener = () => {
    let el: HTMLElement | null = triggerRef.current?.parentElement ?? null;
    while (el && el !== document.body) {
      const { overflow, overflowY } = window.getComputedStyle(el);
      if (
        overflow === "auto" ||
        overflow === "scroll" ||
        overflowY === "auto" ||
        overflowY === "scroll"
      ) {
        break;
      }
      el = el.parentElement;
    }
    if (!el || el === document.body) return;

    const handler = () => pickerRef.current?.close();
    scrollHandlerRef.current = handler;
    scrollParentRef.current = el;
    el.addEventListener("scroll", handler);
  };

  const removeScrollListener = () => {
    if (scrollHandlerRef.current && scrollParentRef.current) {
      scrollParentRef.current.removeEventListener(
        "scroll",
        scrollHandlerRef.current,
      );
      scrollHandlerRef.current = null;
      scrollParentRef.current = null;
    }
  };

  const picker = (
    <CheckTreePicker
      ref={pickerRef}
      placement={isVertical ? "autoHorizontal" : "autoVertical"}
      preventOverflow
      block
      // cascade=false keeps the plugin's parent-selection semantics: the value
      // is the checked node itself and the server expands the subtree. Cascading
      // here would explode a parent into thousands of child ids in the request.
      cascade={false}
      searchable
      // Required: rsuite 4's CheckTree renders the WHOLE tree in the DOM even
      // collapsed, and QA2 has ~30k regions. (Tests mock react-virtualized's
      // AutoSizer — under jsdom it measures 0x0 and renders nothing.)
      virtualized
      labelKey="text"
      valueKey="id"
      childrenKey="children"
      data={tree}
      value={value}
      onOpen={() => {
        if (isVertical) addScrollListener();
        setTimeout(() => {
          const input = document.querySelector(
            ".rs-picker-search-bar-input",
          ) as HTMLInputElement | null;
          input?.focus();
        }, 50);
      }}
      onClose={() => {
        if (isVertical) removeScrollListener();
      }}
      onChange={(selectedIds) => {
        const dataMap = new Map(data.map((item) => [item.id, item]));
        const selectedObjects = ((selectedIds as (string | number)[]) ?? [])
          .map((id) => dataMap.get(id))
          .filter((item): item is RegionNode => item !== undefined)
          .map(({ id, text }) => ({ id, text }));
        setFilterValue(props.filterDef.code, selectedObjects);
      }}
      placeholder={props.placeholder}
      className={value.length > 0 ? "filled" : ""}
    />
  );

  if (isVertical) {
    return <div ref={triggerRef}>{picker}</div>;
  }

  return picker;
};

export default DynamicRegionTreePicker;
